<template>
  <section class="p-6 max-w-5xl mx-auto">
    <div class="flex items-end justify-between mb-5 gap-4 flex-wrap">
      <div>
        <h1 class="font-heading font-bold text-xl">Trigger Library</h1>
        <p class="text-[13px] text-gray-500 dark:text-dark-text-muted mt-1">
          When each message fires — edit times here, changes deploy via Dev
        </p>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-[12px] text-gray-500 dark:text-dark-text-muted"
          >Publishers can edit trigger times</span
        >
        <span class="perm-tag perm-publisher">Publisher only</span>
      </div>
    </div>

    <div
      class="card card-body mb-6 flex gap-3 items-start text-[12.5px] leading-relaxed"
    >
      <info-icon class="w-4 h-4 text-brand-teal flex-shrink-0 mt-0.5" />
      <div>
        Trigger times are shared across all flows that use them. Changing a
        trigger here affects every message tied to it. Publishers can propose
        changes — Dev deploys them to Twilio / Onfleet / Shopify.
        <strong
          >Changes go through the same draft → review → publish workflow as
          message content.</strong
        >
      </div>
    </div>

    <!-- Heads-up batch logic -->
    <div
      class="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
    >
      💬 Heads-up — recipient
    </div>
    <div class="card mb-6">
      <div class="card-header">
        <div class="card-title">Heads-up batch logic</div>
        <div class="text-[12px] text-gray-500 dark:text-dark-text-muted">
          Recipient is assigned to exactly one batch per order
        </div>
      </div>
      <div class="divide-y divide-gray-200 dark:divide-dark-border">
        <!-- Batch 1 -->
        <div
          class="grid md:grid-cols-[180px_1fr_auto] gap-3 items-center px-5 py-4"
        >
          <div>
            <div
              class="inline-block px-2.5 py-1 rounded-md bg-brand-light-blue text-brand-teal-dark dark:bg-brand-teal dark:bg-opacity-20 dark:text-brand-light-blue text-[13px] font-semibold mb-1.5"
            >
              Batch 1
            </div>
            <div class="flex flex-wrap gap-1.5">
              <span class="badge badge-teal text-[11px]"
                >Consented at checkout</span
              >
              <span class="badge badge-teal text-[11px]"
                >Placed before cutoff</span
              >
            </div>
          </div>
          <div>
            <div
              class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-muted"
            >
              Sends at
            </div>
            <div class="text-[13px] font-medium font-mono">
              {{ triggerDisplay("batch1") }}
            </div>
            <div
              class="text-[12px] text-gray-500 dark:text-dark-text-muted mt-1"
            >
              Message says <strong>"tomorrow"</strong> · ⚙️ System trigger
            </div>
          </div>
          <button
            class="btn btn-outline btn-sm"
            @click="openTriggerEdit('batch1')"
          >
            Edit timing
          </button>
        </div>
        <!-- Batch 2 -->
        <div
          class="grid md:grid-cols-[180px_1fr_auto] gap-3 items-center px-5 py-4"
        >
          <div>
            <div
              class="inline-block px-2.5 py-1 rounded-md bg-brand-light-blue text-brand-teal-dark dark:bg-brand-teal dark:bg-opacity-20 dark:text-brand-light-blue text-[13px] font-semibold mb-1.5"
            >
              Batch 2
            </div>
            <div class="flex flex-wrap gap-1.5 items-center">
              <span class="badge badge-gray text-[11px]">Missed Batch 1</span>
              <span class="text-[11px] text-gray-500 dark:text-dark-text-muted"
                >(not consented, or after cutoff)</span
              >
            </div>
          </div>
          <div>
            <div
              class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-muted"
            >
              Sends at
            </div>
            <div class="text-[13px] font-medium font-mono">
              {{ triggerDisplay("batch2") }}
            </div>
            <div
              class="text-[12px] text-gray-500 dark:text-dark-text-muted mt-1"
            >
              Message says <strong>"today"</strong> · ⚙️ System trigger
            </div>
          </div>
          <button
            class="btn btn-outline btn-sm"
            @click="openTriggerEdit('batch2')"
          >
            Edit timing
          </button>
        </div>
        <!-- Batch 3 -->
        <div
          class="grid md:grid-cols-[180px_1fr_auto] gap-3 items-center px-5 py-4"
        >
          <div>
            <div
              class="inline-block px-2.5 py-1 rounded-md bg-brand-light-blue text-brand-teal-dark dark:bg-brand-teal dark:bg-opacity-20 dark:text-brand-light-blue text-[13px] font-semibold mb-1.5"
            >
              Batch 3
            </div>
            <div class="flex flex-wrap gap-1.5 items-center">
              <span class="badge badge-gray text-[11px]"
                >Missed Batch 1 &amp; 2</span
              >
              <span class="text-[11px] text-gray-500 dark:text-dark-text-muted"
                >(placed after 8:00 AM delivery day)</span
              >
            </div>
          </div>
          <div>
            <div
              class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-muted"
            >
              Sends at
            </div>
            <div class="text-[13px] font-medium font-mono">
              {{ staticTriggers.batch3.when }}
            </div>
            <div
              class="text-[12px] text-gray-500 dark:text-dark-text-muted mt-1"
            >
              Message says <strong>"today"</strong> ·
              {{ staticTriggers.batch3.source }}
            </div>
          </div>
          <button
            class="btn btn-ghost btn-sm text-gray-400 cursor-default"
            aria-disabled="true"
          >
            System event
          </button>
        </div>
      </div>
    </div>

    <!-- Other triggers -->
    <div
      class="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
    >
      📦 Other triggers
    </div>
    <div class="card mb-6 divide-y divide-gray-200 dark:divide-dark-border">
      <div
        v-for="row in otherTriggers"
        :key="row.tag"
        class="grid md:grid-cols-[180px_1fr_auto] gap-3 items-center px-5 py-4"
      >
        <div>
          <div
            class="inline-block px-2.5 py-1 rounded-md bg-brand-light-blue text-brand-teal-dark dark:bg-brand-teal dark:bg-opacity-20 dark:text-brand-light-blue text-[13px] font-semibold mb-1.5"
          >
            {{ row.tag }}
          </div>
          <div>
            <span class="badge badge-gray text-[11px]">{{ row.applies }}</span>
          </div>
        </div>
        <div>
          <div
            class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-muted"
          >
            {{ row.label }}
          </div>
          <div class="text-[13px] font-medium font-mono">{{ row.fires }}</div>
          <div class="text-[12px] text-gray-500 dark:text-dark-text-muted mt-1">
            {{ row.note }} · {{ row.source }}
          </div>
        </div>
        <button
          v-if="row.editable"
          class="btn btn-outline btn-sm"
          @click="openTriggerEdit('followup')"
        >
          Edit timing
        </button>
        <button
          v-else
          class="btn btn-ghost btn-sm text-gray-400 cursor-default"
          aria-disabled="true"
        >
          System event
        </button>
      </div>
    </div>

    <!-- Changelog -->
    <div
      class="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
    >
      📝 Recent trigger changes
    </div>
    <div class="card card-body">
      <div class="space-y-3">
        <div v-for="(c, i) in triggerChangelog" :key="i" class="flex gap-2.5">
          <div
            class="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
            :class="
              c.current ? 'bg-brand-teal' : 'bg-gray-300 dark:bg-dark-border'
            "
          ></div>
          <div>
            <div class="text-[12.5px] font-medium">{{ c.user }}</div>
            <div class="text-[11px] text-gray-400 dark:text-dark-text-muted">
              {{ c.meta }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Trigger edit modal -->
    <div
      v-if="modal === 'trigger'"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40"
      @click.self="closeModal"
    >
      <div
        class="card w-full max-w-md p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trigger-modal-title"
      >
        <div
          id="trigger-modal-title"
          class="font-heading font-bold text-base mb-2"
        >
          {{ triggerModal.title }}
        </div>
        <p class="text-[13px] text-gray-500 dark:text-dark-text-muted mb-4">
          This change affects all flows using this trigger. It goes through the
          same draft → review → publish workflow as message content.
        </p>

        <div class="rounded-lg bg-gray-50 dark:bg-dark-surface p-3 mb-4">
          <div
            class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-muted mb-1.5"
          >
            Current setting
          </div>
          <div class="text-[13px] font-mono font-semibold">
            {{ triggerModal.currentVal }}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label class="field-label">New time</label>
            <input type="time" class="field" v-model="triggerModal.newTime" />
          </div>
          <div>
            <label class="field-label">Day</label>
            <select class="field" v-model="triggerModal.newDay">
              <option value="day-before">Day before delivery</option>
              <option value="day-of">Delivery day</option>
            </select>
          </div>
        </div>

        <div class="mb-5">
          <label class="field-label">Reason for change (required)</label>
          <input
            type="text"
            class="field"
            v-model="triggerModal.reason"
            placeholder="e.g. Shifting earlier to improve open rates"
          />
        </div>

        <div
          class="rounded-lg bg-amber-50 dark:bg-amber-900 dark:bg-opacity-20 border border-amber-300 dark:border-amber-700 dark:border-opacity-40 px-3 py-2.5 mb-5 text-[12.5px] text-amber-800 dark:text-amber-300"
        >
          ⚠️ Saved as a draft — must be reviewed and published before going
          live.
        </div>

        <div class="flex justify-end gap-2">
          <button class="btn btn-outline" @click="closeModal">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="!triggerModal.reason.trim()"
            @click="saveTriggerDraft"
          >
            Save as draft
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { InfoIcon } from "vue-feather-icons";
import { useSmsTriggers } from "./composables/useSmsTriggers";

const {
  staticTriggers,
  triggerChangelog,
  otherTriggers,
  modal,
  triggerModal,
  triggerDisplay,
  openTriggerEdit,
  saveTriggerDraft,
  closeModal
} = useSmsTriggers();
</script>
